import React from 'react';
// Update to use new styles structure - CSS is imported from styles/index.js
import './styles/locationList.css';
import { useTsp } from '../context/TspContext';

// Location point type definitions
export const LocationType = {
  // Location point types, can be extended as needed
  NORMAL: 'normal', // Normal location point
  START: 'start',   // Starting point
  END: 'end',       // End point
  WAYPOINT: 'waypoint' // Waypoint
};

// Initialize sample data (optional)
export const initialLocations = [
  { id: '1', name: '1', latitude: -37.8136, longitude: 144.9631, type: LocationType.NORMAL },
  { id: '2', name: '2', latitude: -37.8236, longitude: 144.9531, type: LocationType.NORMAL }
];

const LocationList = ({ 
  locations, 
  startId, 
  mode,
  readOnly = false,       // Whether in read-only mode
  showActions = true,     // Whether to show action buttons
  allowDelete = true,     // Whether to allow location deletion
  allowSetStart = true,   // Whether to allow setting start point
  onLocationChange, 
  onStartIdChange
}) => {
  // Get jump to location functionality from TSP context
  const { jumpToLocation } = useTsp();
  
  // Handle clicking on a location to jump to it on the map
  const handleLocationClick = (location) => {
    if (jumpToLocation) {
      jumpToLocation(location.latitude, location.longitude);
    }
  };

  // Handle location deletion
  const handleDeleteLocation = (id) => {
    if (readOnly) return;
    
    // Create new location list excluding the point to delete
    const newLocations = locations.filter(location => location.id !== id);
    
    // Renumber all location points
    const renumberedLocations = newLocations.map((location, index) => ({
      ...location,
      name: `${index + 1}`
    }));
    
    onLocationChange(renumberedLocations);
    
    // If only one point or no points left after deletion, clear start point
    if (renumberedLocations.length <= 1) {
      onStartIdChange('');
    }
    // If deleted point was start point and multiple points remain, set first point as start
    else if (id === startId) {
      onStartIdChange(renumberedLocations[0].id);
    }
  };

  // Toggle start point setting
  const toggleStartPoint = (id) => {
    if (readOnly) return;
    
    // If already start point, unset it
    if (id === startId) {
      onStartIdChange('');
    } else {
      // Otherwise set as new start point
      onStartIdChange(id);
      
      // Update all location point types
      const updatedLocations = locations.map(location => {
        if (location.id === id) {
          return { ...location, type: LocationType.START };
        } else if (location.type === LocationType.START) {
          return { ...location, type: LocationType.NORMAL };
        }
        return location;
      });
      
      onLocationChange(updatedLocations);
    }
  };

  // Generate location point label
  const getLocationLabel = (location) => {
    // Only show start point label
    if (startId === location.id) {
      return <span className="start-badge">Start</span>;
    }
    return null;
  };

  // Show different empty state message based on mode
  const getEmptyStateMessage = () => {
    switch (mode) {
      case 'Setup Points':
        return 'No location points. Click on the map or enter coordinates below to add points.';
      case 'Custom Path':
        return 'No location points. Please add points in Setup Points mode first.';
      case 'Auto Path':
        return 'No location points. Please add points in Setup Points mode first.';
      default:
        return 'No location points.';
    }
  };

  return (
    <div className="location-list-container">
      <h4 className="location-list-title">Location Points</h4>
      
      {/* Location points list */}
      {locations.length > 0 ? (
        <div className="location-list">
          {locations.map(location => (
            <div key={location.id} className="location-item">
              <div 
                className="location-item-content clickable-location"
                onClick={() => handleLocationClick(location)}
                title="Click to jump to this location on the map"
              >
                <span>
                  {location.name}. {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                  {getLocationLabel(location)}
                </span>
              </div>
              {showActions && (
                <div className="location-item-actions">
                  {/* Start point toggle button */}
                  {!readOnly && allowSetStart && (
                    <button 
                      onClick={() => toggleStartPoint(location.id)}
                      className={`action-btn ${startId === location.id ? 'unset-start-btn' : 'set-start-btn'}`}
                      title={startId === location.id ? "Unset Start" : "Set as Start"}
                    >
                      {startId === location.id ? "⭐Unset" : "⭐Start"}
                    </button>
                  )}
                  {/* Delete button */}
                  {!readOnly && allowDelete && (
                    <button 
                      onClick={() => handleDeleteLocation(location.id)}
                      className="action-btn delete-btn"
                      title="Delete"
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>{getEmptyStateMessage()}</p>
        </div>
      )}
    </div>
  );
};

export default LocationList;
